import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Tabs,
  Form,
  message,
  Drawer,
  Typography,
  Button,
  Modal,
  Tag,
  Image,
  theme,
  Row,
  Col,
} from "antd";
import ClinicalImpressionForm from "../../InspectionForms/ClinicalImpressionForm/ClinicalImpressionForm";
import DrGeneralExaminationFormDR from "../../InspectionForms/Examination/GeneralExaminationForm/DrGeneralExaminationFormDR";
import DrSystemicExaminationFormDR from "../../InspectionForms/Examination/SystemicExaminationForm/DrSystemicExaminationFormDR";
import DrHistoryFormDR from "../../InspectionForms/HistoryForm/DrHistoryFormDR";
import {
  doctorDailyReviewAPI,
  getDoctorDailyReviewAPI,
  getDoctorInspectionAPI,
  getLatestDailyReviewAPI,
  updateDoctorDailyReviewAPI,
} from "../../../../services/doctor.services";
import { LeftOutlined } from "@ant-design/icons";
import { DailyReviewContext } from "./DailyReviewFormProvider";
import { useLocation } from "react-router-dom";
import bedImg from "../../../../assets/images/patientIcons/bed.svg";
import "../InitialInspection/InitialInspectionHeader.css";
import "./DailyReview.css";

const DailyReviewForm = ({
  editId,
  visible,
  setVisible,
  refreshPage,
  iAssessment,
  addMode,
  setAddMode,
  setActiveSegment,
  reviewData,
  setDailyReviewRefresh,
  dailyRefresh,
  segmentData,
  patientData,
}) => {
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const { token } = theme.useToken();
  const [historyForm] = Form.useForm();
  const [generalForm] = Form.useForm();
  const [systemicForm] = Form.useForm();
  const [clinicalForm] = Form.useForm();
  const { formData, setFormData } = useContext(DailyReviewContext);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const formBodyRef = useRef(null);

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);
  const newReview = reviewData?.length === 0;
  const reviewId = reviewData?.[0]?.id;
  const newReviewId = addMode === false && reviewId ? reviewId : null;

  const reviewDate =
    segmentData?.find(
      (option) => option.value === editId && option.value !== "others"
    )?.label ||
    new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit" });

  const handleSubmit = async (values, flag) => {
    try {
      if (editId || newReviewId) {
        await updateDoctorDailyReviewAPI(editId || newReviewId, values);
        setDailyReviewRefresh();
      } else {
        await doctorDailyReviewAPI(values);
        refreshPage();
        setAddMode(false);
        setDailyReviewRefresh();
      }

      message.success(`${flag} submitted successfully`);

      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onHistorySubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        history: values,
        admission_id: params?.patient,
      },
      "History"
    );
  };
  const onGeneralSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        general_examination: values,
        admission_id: params?.patient,
      },
      "General Examination"
    );
  };
  const onSystemicSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        systemic_examination: values,
        admission_id: params?.patient,
      },
      "Systemic Examination"
    );
  };
  const onClinicalSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        clinical_diagnosis: values,
        admission_id: params?.patient,
      },
      "Clinical Diagnosis"
    );
  };

  const openEdit = async () => {
    if (editId && newReview === false) {
      try {
        const response = await getDoctorDailyReviewAPI(editId);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
        message.error("Unable to Fetch Data");
      }
    } else if (newReview === false) {
      try {
        const response = await getLatestDailyReviewAPI(params?.patient);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
        message.error("Unable to Fetch Data");
      }
    } else {
      try {
        const response = await getDoctorInspectionAPI(iAssessment);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error:${error}`);
      }
    }
  };

  useEffect(() => {
    openEdit();
  }, [editId, dailyRefresh]);

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return clinicalForm.submit();
      case "2":
        return historyForm.submit();
      case "3":
        return generalForm.submit();
      case "4":
        return systemicForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      switch (activeTab) {
        case "1":
          clinicalForm.validateFields().then((values) => {
            setIsDirty(false);
            onClinicalSubmit(values);
            setActiveTab(key);
          });
          break;
        case "2":
          historyForm.validateFields().then((values) => {
            setIsDirty(false);
            onHistorySubmit(values);
            setActiveTab(key);
          });
          break;
        case "3":
          generalForm.validateFields().then((values) => {
            setIsDirty(false);
            onGeneralSubmit(values);
            setActiveTab(key);
          });
          break;
        case "4":
          systemicForm.validateFields().then((values) => {
            setIsDirty(false);
            onSystemicSubmit(values);
            setActiveTab(key);
          });
          break;
        default:
          setActiveTab(key);
      }
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  const handleCloseConfirm = () => {
    Modal.confirm({
      title: "Confirmation",
      content: "Are you sure you want to close?",
      okButtonProps: {
        style: {
          backgroundColor: token.colorPrimary,
          borderColor: token.colorPrimary,
        },
      },
      onOk: () => {
        setVisible(false);
        setActiveSegment(reviewData[0]?.id);
      },
    });
  };

  const patientName = `${patientData?.admission?.patient?.first_name || ""} ${
    patientData?.admission?.patient?.last_name || ""
  }`.trim();

  const reviewTabs = [
    { key: "1", label: "Clinical Diagnosis" },
    { key: "2", label: "History" },
    { key: "3", label: "General Examination" },
    { key: "4", label: "Systemic Examination" },
  ];

  return (
    <div>
      <Drawer
        styles={{
          body: {
            overflow: "hidden",
            paddingTop: 0,
            paddingBottom: 0,
            paddingInline: 24,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
        open={visible}
        width="100%"
        title={
          <div className="ia-drawer-header">
            <div className="ia-drawer-header__main">
              <LeftOutlined
                className="ia-drawer-header__back"
                onClick={handleCloseConfirm}
              />
              <div className="ia-drawer-header__identity">
                <div className="ia-drawer-header__name-row">
                  <Typography.Text
                    className="ia-drawer-header__name"
                    ellipsis={{ tooltip: patientName }}
                  >
                    {patientName}
                  </Typography.Text>
                  {patientData?.admission?.bed?.bed_no && (
                    <Tag color="orange" className="ia-drawer-header__bed">
                      <Image src={bedImg} preview={false} width={12} />{" "}
                      {patientData?.admission?.bed?.bed_no}
                    </Tag>
                  )}
                </div>
                <div className="ia-drawer-header__meta">
                  <Tag className="ia-drawer-header__tag">
                    {patientData?.admission?.patient?.age_by_dob ||
                      patientData?.admission?.patient?.age}{" "}
                    {patientData?.admission?.patient?.gender}
                  </Tag>
                  <Tag className="ia-drawer-header__tag">
                    IP ID: {patientData?.admission?.ip_id}
                  </Tag>
                  {reviewDate && (
                    <Tag className="ia-drawer-header__tag">{reviewDate}</Tag>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        footer={null}
        onClose={() => {
          setVisible(false);
          setActiveSegment(reviewData[0]?.id);
        }}
        closeIcon={null}
        keyboard={false}
      >
        <div className="daily-review-form-shell">
          <div className="ia-form-tabs daily-review-form-tabs">
            <Row>
              <Col
                xs={24}
                sm={24}
                md={{ span: 23, offset: 1 }}
                lg={{ span: 21, offset: 3 }}
                xl={{ span: 19, offset: 5 }}
                xxl={{ span: 18, offset: 6 }}
              >
                <Tabs
                  activeKey={activeTab}
                  items={reviewTabs}
                  onChange={handleTabChange}
                />
              </Col>
            </Row>
          </div>

          <div className="ia-form-panel">
            <div className="ia-form-body" ref={formBodyRef}>
              {activeTab === "1" && (
                <Form
                  id="daily-review-clinical-form"
                  form={clinicalForm}
                  layout="vertical"
                  onFinish={onClinicalSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData?.clinical_diagnosis}
                  className="ip-forms ip-forms-review"
                >
                  <ClinicalImpressionForm
                    form={clinicalForm}
                    context={DailyReviewContext}
                    editId={editId}
                    patientData={patientData}
                  />
                </Form>
              )}

              {activeTab === "2" && (
                <Form
                  id="daily-review-history-form"
                  form={historyForm}
                  layout="vertical"
                  onFinish={onHistorySubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData?.history}
                  className="ip-forms ip-forms-review"
                >
                  <DrHistoryFormDR form={historyForm} />
                </Form>
              )}

              {activeTab === "3" && (
                <Form
                  id="daily-review-general-form"
                  form={generalForm}
                  layout="vertical"
                  onFinish={onGeneralSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData?.general_examination}
                  className="ip-forms ip-forms-review"
                >
                  <DrGeneralExaminationFormDR form={generalForm} />
                </Form>
              )}

              {activeTab === "4" && (
                <Form
                  id="general-form"
                  form={systemicForm}
                  layout="vertical"
                  onFinish={onSystemicSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData?.systemic_examination}
                  className="ip-forms ip-forms-review"
                >
                  <DrSystemicExaminationFormDR form={systemicForm} />
                </Form>
              )}
            </div>

            <div className="ia-form-footer">
              {activeTab !== "4" && (
                <Button
                  onClick={() => {
                    getFormKey();
                    setVisible(false);
                  }}
                  style={{ marginRight: 8 }}
                >
                  Save & Close
                </Button>
              )}
              <Button
                onClick={() => {
                  getFormKey();
                  document.querySelector(".ip-forms")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                  const nextTab = (parseInt(activeTab) + 1).toString();
                  if (activeTab === "4") {
                    setVisible(false);
                  } else {
                    setActiveTab(nextTab);
                  }
                }}
                type="primary"
              >
                {activeTab === "4" ? "Done" : " Save & Proceed"}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default DailyReviewForm;
